import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';
import { DURATION_NORMAL, EASE_OUT } from '@/lib/motion';

export interface TimeValue {
  hour: number; // 1–12
  minute: number; // 0–59
  meridiem: 'AM' | 'PM';
}

interface ClockTimePickerProps {
  value: TimeValue;
  onChange: (value: TimeValue) => void;
}

type Mode = 'hours' | 'minutes';

// Clock face geometry (px). h-44/w-44 == 176px.
const CLOCK = 176;
const CENTER = CLOCK / 2;
const NUMBER_RADIUS = 70;

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

/** Place a unit on the dial. 0° points to 12 o'clock, increasing clockwise. */
function polar(angleDeg: number, radius: number): { x: number; y: number } {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: CENTER + radius * Math.sin(rad),
    y: CENTER - radius * Math.cos(rad),
  };
}

const hourAngle = (h: number) => (h % 12) * 30;
const minuteAngle = (m: number) => m * 6;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Landscape time picker: overline title on top (aligned with the calendar's),
 * then the digital readout + steppers centered beside the dial.
 */
const ClockTimePicker: React.FC<ClockTimePickerProps> = ({ value, onChange }) => {
  const [mode, setMode] = useState<Mode>('hours');
  const reduceMotion = useReducedMotion();

  // Draft text for the manual hh/mm inputs, re-synced whenever the value
  // changes from the dial/steppers so typing and clicking never fight.
  const [hourText, setHourText] = useState(() => pad(value.hour));
  const [minuteText, setMinuteText] = useState(() => pad(value.minute));
  useEffect(() => setHourText(pad(value.hour)), [value.hour]);
  useEffect(() => setMinuteText(pad(value.minute)), [value.minute]);

  const typeHour = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(-2);
    setHourText(digits);
    const n = parseInt(digits, 10);
    if (Number.isFinite(n) && n >= 1 && n <= 12) onChange({ ...value, hour: n });
  };

  const typeMinute = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(-2);
    setMinuteText(digits);
    const n = parseInt(digits, 10);
    if (Number.isFinite(n) && n >= 0 && n <= 59) onChange({ ...value, minute: n });
  };

  const activeAngle = mode === 'hours' ? hourAngle(value.hour) : minuteAngle(value.minute);
  const knob = polar(activeAngle, NUMBER_RADIUS);

  const handTransition = reduceMotion
    ? { duration: 0 }
    : { duration: DURATION_NORMAL, ease: EASE_OUT };

  const step = (dir: 1 | -1) => {
    if (mode === 'hours') {
      const next = ((value.hour - 1 + dir + 12) % 12) + 1;
      onChange({ ...value, hour: next });
    } else {
      const next = (value.minute + dir + 60) % 60;
      onChange({ ...value, minute: next });
    }
  };

  const numbers = mode === 'hours' ? HOURS : MINUTES;
  const stepUnit = mode === 'hours' ? 'hour' : 'minute';

  return (
    <div className="flex h-full w-full flex-col">
      <p className="text-xs font-bold uppercase tracking-wider text-text-secondary">Select time</p>

      <div className="flex flex-1 items-center gap-5 pt-2 sm:gap-6">
        {/* Left rail: digital readout + steppers, centered beside the dial */}
        <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
          {/* Digital readout — focus hour / minute to choose what the dial edits */}
          <div className="flex items-start gap-3">
            <div className="flex items-baseline">
              {/* Editable readout: focus picks what the dial edits, typing sets it */}
              <input
                type="text"
                inputMode="numeric"
                value={hourText}
                onChange={(e) => typeHour(e.target.value)}
                onFocus={(e) => {
                  setMode('hours');
                  e.target.select();
                }}
                onBlur={() => setHourText(pad(value.hour))}
                aria-label="Hour"
                className={cn(
                  'w-[2ch] rounded bg-transparent text-right text-3xl font-bold leading-none tabular-nums transition-colors focus-ring',
                  mode === 'hours'
                    ? 'text-text-primary'
                    : 'text-text-secondary hover:text-text-primary'
                )}
              />
              <span className="text-3xl font-bold leading-none text-text-primary">:</span>
              <input
                type="text"
                inputMode="numeric"
                value={minuteText}
                onChange={(e) => typeMinute(e.target.value)}
                onFocus={(e) => {
                  setMode('minutes');
                  e.target.select();
                }}
                onBlur={() => setMinuteText(pad(value.minute))}
                aria-label="Minutes"
                className={cn(
                  'w-[2ch] rounded bg-transparent text-left text-3xl font-bold leading-none tabular-nums transition-colors focus-ring',
                  mode === 'minutes'
                    ? 'text-text-primary'
                    : 'text-text-secondary hover:text-text-primary'
                )}
              />
            </div>
            <div className="flex flex-col gap-0.5 text-base font-semibold leading-tight">
              {(['AM', 'PM'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => onChange({ ...value, meridiem: m })}
                  aria-pressed={value.meridiem === m}
                  className={cn(
                    'rounded px-0.5 transition-colors focus-ring',
                    value.meridiem === m
                      ? 'text-text-primary'
                      : 'text-text-secondary hover:text-text-primary'
                  )}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div className="-ml-2 flex items-center gap-1">
            <button
              type="button"
              onClick={() => step(-1)}
              aria-label={`Previous ${stepUnit}`}
              className="rounded-full p-2 text-text-secondary transition-colors hover:bg-primary-500/10 hover:text-text-primary focus-ring active:scale-[0.97]"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              aria-label={`Next ${stepUnit}`}
              className="rounded-full p-2 text-text-secondary transition-colors hover:bg-primary-500/10 hover:text-text-primary focus-ring active:scale-[0.97]"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Clock dial */}
        <div className="shrink-0">
          <div
            className="relative h-44 w-44 select-none"
            role="group"
            aria-label={`Select ${stepUnit}`}
          >
            <div className="absolute inset-0 rounded-full bg-grey-100 dark:bg-grey-800" />

            <svg
              className="pointer-events-none absolute inset-0 h-full w-full"
              viewBox={`0 0 ${CLOCK} ${CLOCK}`}
              aria-hidden
            >
              <motion.line
                x1={CENTER}
                y1={CENTER}
                className="stroke-primary-500"
                strokeWidth={2}
                strokeLinecap="round"
                initial={{ x2: knob.x, y2: knob.y }}
                animate={{ x2: knob.x, y2: knob.y }}
                transition={handTransition}
              />
              <motion.circle
                r={16}
                className="fill-primary-500"
                initial={{ cx: knob.x, cy: knob.y }}
                animate={{ cx: knob.x, cy: knob.y }}
                transition={handTransition}
              />
              <circle cx={CENTER} cy={CENTER} r={4} className="fill-primary-500" />
            </svg>

            <div className="absolute inset-0">
              {numbers.map((n) => {
                const angle = mode === 'hours' ? hourAngle(n) : minuteAngle(n);
                const pos = polar(angle, NUMBER_RADIUS);
                const selected = mode === 'hours' ? value.hour === n : value.minute === n;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() =>
                      onChange(mode === 'hours' ? { ...value, hour: n } : { ...value, minute: n })
                    }
                    aria-pressed={selected}
                    aria-label={mode === 'hours' ? `${n} o'clock` : `${n} minutes`}
                    style={{
                      left: `${pos.x}px`,
                      top: `${pos.y}px`,
                      transform: 'translate(-50%, -50%)',
                    }}
                    className={cn(
                      'absolute flex h-8 w-8 items-center justify-center rounded-full text-sm tabular-nums transition-colors focus-ring',
                      selected
                        ? 'font-semibold text-primary-ink'
                        : 'text-text-primary hover:bg-primary-500/10'
                    )}
                  >
                    {mode === 'hours' ? n : pad(n)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClockTimePicker;
